import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import api, { apiErrorMessage } from "@/lib/api";
import { toast } from "sonner";
import { Sparkles, ArrowRight } from "lucide-react";

export default function AdminDashboard() {
  const [stats, setStats] = useState({ clients: 0, projects: 0, packages: 0, leads: 0 });
  const [recentProjects, setRecentProjects] = useState([]);
  const [seeding, setSeeding] = useState(false);

  const load = async () => {
    try {
      const [c, p, k] = await Promise.all([
        api.get("/admin/clients"),
        api.get("/admin/projects"),
        api.get("/admin/packages"),
      ]);
      setStats({
        clients: c.data.length,
        projects: p.data.length,
        packages: k.data.length,
        leads: 0,
      });
      setRecentProjects(p.data.slice(0, 5));
    } catch (e) {
      toast.error(apiErrorMessage(e));
    }
  };

  useEffect(() => { load(); }, []);

  const seedDemo = async () => {
    setSeeding(true);
    try {
      const r = await api.post("/admin/demo/seed");
      toast.success("Demo project seeded");
      await load();
      window.open(`/p/${r.data.slug}`, "_blank");
    } catch (e) {
      toast.error(apiErrorMessage(e));
    } finally {
      setSeeding(false);
    }
  };

  return (
    <div className="px-8 py-8 max-w-7xl">
      <div className="flex items-end justify-between mb-8">
        <div>
          <div className="text-[11px] uppercase tracking-[0.3em] text-zinc-500">Studio</div>
          <h1 className="font-display text-4xl tracking-tight mt-1">Dashboard</h1>
        </div>
        <button
          onClick={seedDemo}
          disabled={seeding}
          data-testid="seed-demo-btn"
          className="inline-flex items-center gap-2 px-4 py-2 rounded-md bg-[#C4A47C] text-zinc-900 text-sm font-medium hover:opacity-90 transition disabled:opacity-60"
        >
          <Sparkles className="h-4 w-4" />
          {seeding ? "Seeding…" : "Seed demo project"}
        </button>
      </div>

      <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-10">
        {[
          { label: "Clients", value: stats.clients, testid: "stat-clients" },
          { label: "Projects", value: stats.projects, testid: "stat-projects" },
          { label: "Packages", value: stats.packages, testid: "stat-packages" },
          { label: "Status", value: "Live", testid: "stat-status" },
        ].map((s) => (
          <div key={s.label} className="rounded-xl border border-zinc-200 bg-white p-5">
            <div className="text-[10px] uppercase tracking-[0.25em] text-zinc-500">{s.label}</div>
            <div className="font-display text-3xl mt-2" data-testid={s.testid}>{s.value}</div>
          </div>
        ))}
      </div>

      <div className="rounded-xl border border-zinc-200 bg-white">
        <div className="px-5 py-4 border-b border-zinc-200 flex items-center justify-between">
          <div className="font-display text-lg">Recent projects</div>
          <Link to="/studio/projects" className="text-xs text-zinc-600 hover:text-zinc-900 inline-flex items-center gap-1" data-testid="view-all-projects">
            View all <ArrowRight className="h-3 w-3" />
          </Link>
        </div>
        <div className="divide-y divide-zinc-100">
          {recentProjects.length === 0 && (
            <div className="px-5 py-8 text-sm text-zinc-500" data-testid="empty-projects">
              No projects yet. Click "Seed demo project" to create one instantly.
            </div>
          )}
          {recentProjects.map((p) => (
            <Link
              key={p.id}
              to={`/studio/projects/${p.id}`}
              data-testid={`project-row-${p.slug}`}
              className="flex items-center justify-between px-5 py-4 hover:bg-zinc-50 transition"
            >
              <div>
                <div className="font-medium">{p.name}</div>
                <div className="text-xs text-zinc-500 mt-0.5">/{p.slug}</div>
              </div>
              <div className="flex items-center gap-3">
                <span className={`text-xs px-2 py-1 rounded ${p.is_published ? "bg-emerald-100 text-emerald-800" : "bg-zinc-100 text-zinc-600"}`}>
                  {p.is_published ? "Published" : "Draft"}
                </span>
                <ArrowRight className="h-4 w-4 text-zinc-400" />
              </div>
            </Link>
          ))}
        </div>
      </div>
    </div>
  );
}
